import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { mdiAlert } from "@mdi/js";
import Icon from "@mdi/react";
import { Alert, Button, Flex, Input, notification } from "antd";
import { observer } from "mobx-react-lite";

import { api } from "../../api";
import AuthCard from "../../components/auth/AuthCard";
import { signupStore } from "../../stores/signupStore";
import { usePageTitle } from "../../utils/usePageTitle";
import { usePendingAction } from "../../utils/usePendingAction";

import "./EmailVerificationPage.css";

import type { ConfirmEmailErrorCode } from "../../api/auth";

const RESEND_TIMEOUT = 60;

function formatTime(seconds: number) {
  const m = String(Math.floor(seconds / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${m}:${s}`;
}

export const EmailVerificationPage = observer(function EmailVerificationPage() {
  const { t } = useTranslation();
  usePageTitle(t("signup.verifyEmail.title"));
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [codeRequired, setCodeRequired] = useState(false);
  const [verifyErrorCode, setVerifyErrorCode] =
    useState<ConfirmEmailErrorCode | null>(null);
  const [isResent, setIsResent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_TIMEOUT);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { pending: loading, run: runVerify } = usePendingAction();
  const { pending: resendLoading, run: runResend } = usePendingAction();

  useEffect(() => {
    if (!signupStore.token) {
      navigate("/signup/0", { replace: true });
      return;
    }
    startTimer();
    return () => clearInterval(intervalRef.current ?? undefined);
  }, [navigate]);

  function startTimer() {
    setSecondsLeft(RESEND_TIMEOUT);
    clearInterval(intervalRef.current ?? undefined);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current ?? undefined);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const FATAL_CODES: ConfirmEmailErrorCode[] = [
    "confirmation_expired",
    "token_not_found",
    "wrong_token",
    "max_confirmation_attempts_exceeded",
  ];

  async function handleVerify() {
    if (!code.trim()) {
      setCodeRequired(true);
      return;
    }
    await runVerify(async () => {
      setVerifyErrorCode(null);
      try {
        const result = await api.auth.confirmEmail(code, signupStore.token);
        if (result.ok) {
          navigate("/signup/details/1");
        } else {
          const errorCode = result.code ?? "token_not_found";
          setVerifyErrorCode(errorCode);
          if (FATAL_CODES.includes(errorCode)) {
            setSecondsLeft(0);
            clearInterval(intervalRef.current ?? undefined);
          }
        }
      } catch {
        notification.error({
          message: t("errors.shared.common_error"),
          placement: "topRight",
        });
      }
    });
  }

  async function handleResend() {
    await runResend(async () => {
      setCode("");
      setVerifyErrorCode(null);
      try {
        const result = await api.auth.registerEmail(signupStore.email);
        if (result.ok) {
          signupStore.set(signupStore.email, result.token);
          startTimer();
          setIsResent(true);
          setTimeout(() => setIsResent(false), 3000);
        }
      } catch {
        notification.error({
          message: t("errors.shared.common_error"),
          placement: "topRight",
        });
      }
    });
  }

  return (
    <AuthCard>
      <Flex justify="space-between" align="start">
        <h1>{t("signup.verifyEmail.title")}</h1>
        <div className="email-verification__another-email-wrapper">
          <Link
            to="/signup/0"
            onClick={() => signupStore.clear()}
            className="email-verification__another-email"
          >
            {t("signup.verifyEmail.anotherEmail")}
          </Link>
        </div>
      </Flex>

      <p>
        <Trans
          i18nKey="signup.verifyEmail.description"
          values={{ email: signupStore.email }}
          components={{ strong: <strong /> }}
        />
      </p>

      <Input
        placeholder={t("signup.verifyEmail.codePlaceholder")}
        size="large"
        value={code}
        status={codeRequired ? "error" : ""}
        onChange={(e) => {
          setCode(e.target.value);
          if (verifyErrorCode) setVerifyErrorCode(null);
          if (codeRequired) setCodeRequired(false);
        }}
        onPressEnter={handleVerify}
        autoComplete="one-time-code"
        inputMode="numeric"
        className="email-verification__code-input"
      />
      {codeRequired && (
        <div className="email-verification__field-error">
          {t("signup.details.required")}
        </div>
      )}

      {verifyErrorCode && (
        <>
          <Alert
            type="error"
            showIcon
            icon={<Icon path={mdiAlert} size={0.75} color="#ff4d4f" />}
            style={{ marginTop: 16 }}
            className="email-verification__alert"
            classNames={{
              description: "email-verification__alert-description",
            }}
            description={t(`signup.verifyEmail.errors.${verifyErrorCode}`)}
          />
        </>
      )}

      <Button
        type="primary"
        size="large"
        block
        loading={loading}
        disabled={resendLoading}
        style={{ marginTop: 16 }}
        onClick={handleVerify}
      >
        {t("signup.verifyEmail.verify")}
      </Button>

      <div className="email-verification__timer">
        {t("signup.verifyEmail.resendIn")} {formatTime(secondsLeft)}
      </div>

      {isResent ? (
        <Button size="large" block className="email-verification__resent-btn">
          {t("signup.verifyEmail.codeResent")}
        </Button>
      ) : (
        <Button
          size="large"
          block
          disabled={secondsLeft > 0 || loading}
          loading={resendLoading}
          onClick={handleResend}
        >
          {t("signup.verifyEmail.resend")}
        </Button>
      )}
    </AuthCard>
  );
});
